"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _react = _interopRequireDefault(require("react"));

var _classnames = _interopRequireDefault(require("classnames"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _reactAriaModal = _interopRequireDefault(require("react-aria-modal"));

var _tooltip = _interopRequireDefault(require("../tooltip"));

var _icon = _interopRequireDefault(require("../icon"));

var _getWindow = _interopRequireDefault(require("../utils/get-window"));

var _modalActions = _interopRequireDefault(require("./modal-actions"));

var _eventTrap = _interopRequireDefault(require("./event-trap"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/**
 * An accessible modal dialog.
 *
 * To get a standard button arrangement at the bottom of the modal, use the
 * `primaryAction`, `secondaryAction`, and `tertiaryAction` props.
 *
 * This modal *traps focus within it*. You should be aware of that, because it
 * can sometimes introduce a hurdle when integrating the modal with other
 * things, especially third-party libraries. But it's an essential UX feature.
 */
class Modal extends _react.default.Component {
  constructor(props) {
    super(props);
    this.setDialogEl = this.setDialogEl.bind(this);
  }

  componentDidMount() {
    // After the modal has mounted, it will receive focus (unless initialFocus)
    // is set. If it is taller than the viewport, the browser will scroll down
    // so the top of the modal is at the top of the viewport, instead of
    // staying at the top where you can see the nice padding. To show the
    // padding, we get the underlay element and scroll to the top of that
    // right after the component mounts.
    if (!this.dialogEl || typeof window === 'undefined') return; // A slight delay is necessary so this happens after focus is placed.

    this.scrollTimeout = (0, _getWindow.default)().setTimeout(() => {
      const offsetParent = this.dialogEl.offsetParent;

      if (offsetParent.tagName === 'BODY' || offsetParent.tagName === 'HTML') {
        return;
      }

      offsetParent.scrollTop = 0;
    }, 0);
  }

  componentWillUnmount() {
    if (typeof window === 'undefined') return;
    (0, _getWindow.default)().clearTimeout(this.scrollTimeout);
  }

  setDialogEl(el) {
    this.dialogEl = el;
  }

  renderActions() {
    const {
      primaryAction,
      secondaryAction,
      tertiaryAction
    } = this.props;

    if (!primaryAction) {
      return null;
    }

    return /*#__PURE__*/_react.default.createElement("div", {
      className: "mt24"
    }, /*#__PURE__*/_react.default.createElement(_modalActions.default, {
      primaryAction: primaryAction,
      secondaryAction: secondaryAction,
      tertiaryAction: tertiaryAction
    }));
  }

  render() {
    const {
      props
    } = this;
    let closeButton = null;

    if (props.onExit) {
      closeButton = /*#__PURE__*/_react.default.createElement("div", {
        className: "absolute top right"
      }, /*#__PURE__*/_react.default.createElement(_tooltip.default, {
        block: true,
        content: "Close"
      }, /*#__PURE__*/_react.default.createElement("button", {
        "aria-label": "Close",
        type: "button",
        className: "btn btn--transparent unround-t unround-br color-gray py12 px12",
        onClick: props.onExit,
        "data-test": "modal-close"
      }, /*#__PURE__*/_react.default.createElement(_icon.default, {
        name: "close"
      }))));
    }

    let widthClass = '';

    if (props.size === 'small') {
      widthClass = 'w360';
    } else if (props.size === 'large') {
      widthClass = 'w600';
    }

    const containerClasses = (0, _classnames.default)(`relative wmax-full ${widthClass} bg-white round`, {
      'px36 py36': props.padding === 'large'
    });

    const dialogBody = /*#__PURE__*/_react.default.createElement("div", {
      ref: this.setDialogEl,
      className: containerClasses
    }, props.children, this.renderActions(), closeButton);

    const modalProps = {
      titleText: props.accessibleTitle,
      getApplicationNode: props.getApplicationNode,
      underlayProps: {
        'data-popover-ignore-clicks': true
      },
      underlayClass: 'bg-darken50 px12 py12 px60-mm py60-mm ',
      underlayStyle: {
        zIndex: 1
      },
      alert: props.alert
    };

    if (props.onExit) {
      modalProps.onExit = props.onExit;
    }

    if (props.initialFocus) {
      modalProps.initialFocus = props.initialFocus;
    } else {
      modalProps.focusDialog = true;
    }

    if (props.focusTrapPaused) {
      modalProps.focusTrapPaused = true;
    }

    if (!props.allowEventBubbling) {
      // stopPropagation prevents child modals from closing parent modals when nesting
      return /*#__PURE__*/_react.default.createElement(_eventTrap.default, null, /*#__PURE__*/_react.default.createElement(_reactAriaModal.default, modalProps, dialogBody));
    }

    return /*#__PURE__*/_react.default.createElement(_reactAriaModal.default, modalProps, dialogBody);
  }

}

exports.default = Modal;
Modal.propTypes = {
  /**
   * A screen-reader-friendly modal title. Required for accessibility.
   *
   * This **will not be displayed.** It's only for screen readers.
   * You can visually display your own header text however you'd like.
   */
  accessibleTitle: _propTypes.default.string.isRequired,

  /**
   * Invoked when the modal should close. When this callback is provided,
   * a close button will be in the top right corner, and a click on the underlay
   * or the Escape key will close the modal.
   *
   * If this prop is not provided, the close button will not be present and
   * a click on the underlay or Escape will not close the modal. The reason
   * you might not provide this function is that you want to force the user
   * to do something, instead of allowing them to sneak out of the modal.
   */
  onExit: _propTypes.default.func,

  /**
   * A function that should the primary application node, which should be
   * `aria-hidden` when the modal is open. By default, returns
   * `document.getElementById('app')`.
   */
  getApplicationNode: _propTypes.default.func,

  /**
   * Modal container size. Options are `small`, `large`, or `auto`. If `auto`
   * is provided, a width is not specified.
   */
  size: _propTypes.default.oneOf(['small', 'large', 'auto']),

  /**
   * Selector for a specific element that should receive initial focus. The
   * value will be passed to `querySelector`.
   */
  initialFocus: _propTypes.default.string,

  /**
   * The content of the modal.
   */
  children: _propTypes.default.node.isRequired,

  /**
   * `'large'` or `'none'`.
   */
  padding: _propTypes.default.oneOf(['large', 'none']),

  /**
   * If `true`, this will allow interaction with elements outside of the
   * modal container. You normally don't want to set this, but it can be
   * useful for nesting different components that are displaced to other
   * parts of the DOM.
   */
  focusTrapPaused: _propTypes.default.bool,

  /**
   * If `true`, the modal will have the accessibility props of an alert modal.
   * (Only affects screen readers.)
   */
  alert: _propTypes.default.bool,

  /**
   * The modal's primary action. If this is provided, an encouraging
   * button will be rendered at the bottom of the modal.
   *
   * Provide this and other action props if you want a standard button
   * arrangement at the bottom of the modal. If you need a more custom
   * arrangement, leave them out and insert your buttons into the content.
   *
   * The value is an object with the following properties:
   * - `text`: **(required)** The text of the button.
   * - `callback`: **(required)** Invoked when the button is clicked.
   * - `destructive`: If `true`, the [Button](#button) will be primed for
   *   desctruction.
   */
  primaryAction: _propTypes.default.shape({
    text: _propTypes.default.string.isRequired,
    callback: _propTypes.default.func.isRequired,
    destructive: _propTypes.default.bool
  }),

  /**
   * The modal's secondary action. If this is provided, a discouraging button
   * will be rendered at the bottom of the modal. See the description of
   * `primaryAction`.
   *
   * **Can only be used in combination with `primaryAction`.**
   *
   * The value is an object with the following properties:
   * - `text`: **(required)** The text of the button.
   * - `callback`: **(required)** Invoked when the button is clicked.
   */
  secondaryAction: _propTypes.default.shape({
    text: _propTypes.default.string.isRequired,
    callback: _propTypes.default.func.isRequired
  }),

  /**
   * The modal's tertiary action. **You should rarely if ever need this.**
   * If this is provided, a *very* discouraging button
   * will be rendered at the bottom of the modal. See the description of
   * `primaryAction`.
   *
   * **Can only be used in combination with `primaryAction` and
   * `secondaryAction`.**
   *
   * The value is an object with the following properties:
   * - `text`: **(required)** The text of the button.
   * - `callback`: **(required)** Invoked when the button is clicked.
   */
  tertiaryAction: _propTypes.default.shape({
    text: _propTypes.default.string.isRequired,
    callback: _propTypes.default.func.isRequired
  }),

  /**
   * We stop propagation on clicks by default to enable more intuitive
   * operation with React Portals. If you need click events to bubble up
   * to parent components, set this prop to true
   */
  allowEventBubbling: _propTypes.default.bool
};
Modal.defaultProps = {
  size: 'large',
  getApplicationNode: () => document.getElementById('app'),
  padding: 'large'
};